import { useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useBoardState, useStore } from '../store/context'
import { needsYou } from '../store/store'
import { landingsAwaitingPerson } from '../sessions/attention'
import { useProjectTitle } from '../components/useDocumentTitle'
import { BoardColumns } from './BoardColumns'
import { FilterBar } from './FilterBar'
import { deriveColumns } from './filters'
import { NewIssue, untouched } from './NewIssue'
import { deriveSummary, Summary } from './Summary'
import { useBoardView } from './useBoardView'
import { toggle } from './view'
import { isSheet, pageOf, sameKey } from '../lib/keys'
import { confirmDiscard, hasUnsavedChanges } from '../components/unsaved'
import type { Issue } from '../api/types'
import { ownColumns } from '../vocab'
import { useTabScope } from '../workspace/context'
import { BoardInteractionContext, cardElement, nextCard } from './interaction'
import { Peek } from './Peek'
import { ApprovalStrip } from './ApprovalStrip'
import { QueueDetailStrip } from '../queue/QueueDetail'
import { ReadyColumns } from './ReadyColumns'
import { DeployColumns } from './DeployColumns'
import { deployColumns } from './deploy'
import { proposalAnchor } from '../queue/words'
import { keyId } from '../lib/keys'
import { BoardDigest } from './Digest'
import { mainFailure, MainCheckFailure } from './MainCheckFailure'
import { DeviceNotifications } from '../push/DeviceNotifications'
import { PlanStrip } from '../plans/PlanStrip'

export function BoardPage() {
  useProjectTitle('Board')
  const store = useStore()
  const { issues, projects, selectedProjectId, labels, questions, pendingAnswers, me, sessions, landings, held, dismissed, proposals, returned, plans, runs } = useBoardState()
  const [view, update] = useBoardView()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const scope = useTabScope()
  const [selected, select] = useState<string | null>(null)
  const page = useRef<HTMLDivElement>(null)
  useNamedProposal(page)
  const own = useMemo(() => [...issues.values()].filter(i => i.project === selectedProjectId), [issues, selectedProjectId])
  const ownLabels = useMemo(() => new Map([...labels].filter(([, l]) => l.project === selectedProjectId)), [labels, selectedProjectId])
  const failedMain = useMemo(() => {
    const project = projects.get(selectedProjectId ?? '')
    return project ? mainFailure(project, runs.values()) : undefined
  }, [projects, selectedProjectId, runs])
  const summary = useMemo(() => deriveSummary(selectedProjectId, issues, sessions, needsYou(questions, me?.member?.id, pendingAnswers), landings, proposals, returned, Boolean(failedMain), landingsAwaitingPerson(landings, held, dismissed), projects.get(selectedProjectId ?? '')?.release_map ?? null,
    projects.get(selectedProjectId ?? '')?.landing_policy?.review === 'person'),
    [selectedProjectId, issues, sessions, questions, pendingAnswers, me, landings, held, dismissed, proposals, returned, failedMain, projects])
  // The plan the board is narrowed to lists its issues by key, of any project: this project's show.
  const plan = view.plan ? plans.get(view.plan) : undefined
  const planKeys = useMemo(() => (view.plan ? new Set((plan?.issues ?? []).flatMap((listing) => keyId(listing.key) ?? [])) : null), [view.plan, plan])
  const columns = (view.live === 'deploy' ? deployColumns : deriveColumns)(own, view, { labels: ownLabels, live: summary.ids, planKeys }, Date.now(), ownColumns(projects.get(selectedProjectId ?? '')))
  const togglePlan = (id: string) => update((v) => ({ ...v, plan: v.plan === id ? null : id }))
  const peek = params.get('peek')
  const enlarged = peek ? own.find(issue => sameKey(issue.key, peek) || issue.id === peek) : undefined
  const enlarge = (id: string) => {
    const issue = issues.get(id)
    if (!issue || issue.project !== selectedProjectId) return
    select(id)
    if (enlarged?.id === id) return
    setParams(prev => { const next = new URLSearchParams(prev); next.set('peek', issue.key); return next }, { state: { boardPeek: true } })
  }
  // The issue New issue just filed, while its enlarged view stays on it.
  const fresh = enlarged && location.state?.newIssue === enlarged.id ? enlarged : undefined
  const openNew = (issue: Issue) => {
    select(issue.id)
    setParams(prev => { const next = new URLSearchParams(prev); next.set('peek', issue.key); return next }, { state: { boardPeek: true, newIssue: issue.id } })
  }
  // An edit the enlarged view holds asks first; a new issue left untouched goes to Cancelled.
  const leavePeek = () => {
    if (hasUnsavedChanges([pageOf(location.pathname)]) && !confirmDiscard()) return false
    if (fresh && untouched(fresh)) void store.moveIssue(fresh.id, 'cancelled')
    return true
  }
  const closePeek = () => {
    if (!leavePeek()) return
    if (location.state?.boardPeek) navigate(-1)
    else setParams(prev => { const next = new URLSearchParams(prev); next.delete('peek'); return next }, { replace: true, state: location.state })
  }
  const openPeekPage = () => {
    if (!enlarged) return
    const path = `/${encodeURIComponent(enlarged.key)}`
    if (location.state?.boardPeek) navigate(path, { replace: true })
    else {
      const next = new URLSearchParams(params)
      next.delete('peek')
      navigate({ pathname: location.pathname, search: next.toString() ? `?${next}` : '', hash: location.hash }, { replace: true, state: location.state })
      navigate(path)
    }
  }
  const order = columns.map(column => column.items.filter(issue => store.isIssueConfirmed(issue.id)).map(issue => issue.id))
  const step = (id: string, key: string) => {
    const next = nextCard(order, id, key)
    if (!next) return
    select(next)
    const card = cardElement(next, scope?.surface() ?? document)
    card?.focus()
    card?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }
  const stepPeek = (key: string) => {
    if (!enlarged) return
    const id = nextCard(order, enlarged.id, key)
    const issue = id ? issues.get(id) : undefined
    if (!issue || issue.id === enlarged.id || !leavePeek()) return
    select(issue.id)
    setParams(prev => { const next = new URLSearchParams(prev); next.set('peek', issue.key); return next }, { replace: true, state: { ...location.state, newIssue: undefined } })
  }
  if (!selectedProjectId || !projects.has(selectedProjectId)) return <div className="board-empty">
    <p>This board has no project yet.</p><Link className="button button-primary" to="/new-project">New project</Link>
  </div>
  return <BoardInteractionContext.Provider value={{ selected, select, enlarge, step }}><div className="board-page" ref={page}>
    <Summary summary={summary} filter={view.live} onFilter={live => update(v => ({ ...v, live }))} active={own.length > 0 || Boolean(failedMain)}
      digest={<BoardDigest key={`${me?.member?.id}:${selectedProjectId}`} project={selectedProjectId} />}
      newIssue={<NewIssue key={selectedProjectId} projectId={selectedProjectId} compact={own.length > 0} onCreated={openNew} />} />
    <MainCheckFailure project={projects.get(selectedProjectId)!} />
    <ApprovalStrip project={selectedProjectId} />
    <QueueDetailStrip key={selectedProjectId} project={selectedProjectId} />
    {own.length === 0 ? <p className="board-empty">No issues yet.</p> : <>
      <PlanStrip selected={view.plan} onToggle={togglePlan} />
      <FilterBar view={view} onChange={update} plan={plan} shown={columns.reduce((n, c) => n + c.items.length, 0)} />
      {view.live === 'ready' ? <ReadyColumns columns={columns.filter(c => !c.collapsed)} />
        : view.live === 'deploy' ? <DeployColumns columns={columns} />
        : <BoardColumns columns={columns} sort={view.sort} onToggleShowAll={key => update(v => ({ ...v, showAll: toggle(v.showAll, key) }))}
          onExpandClosed={() => update(v => ({ ...v, closed: true }))} />}
    </>}
    <DeviceNotifications key={`${me?.workspace.id}/${me?.member?.id}`} />
    {enlarged && !isSheet(location.pathname) && <Peek issue={enlarged} fresh={Boolean(fresh)} onClose={closePeek} onStep={stepPeek} onOpenPage={openPeekPage} />}
  </div></BoardInteractionContext.Provider>
}

/**
 * A link to one proposal (`#proposal-<id>`, queue/words.ts) scrolls to its
 * card under Ready to merge, or to its approval's strip, which open
 * themselves. It may arrive from the feed after the page.
 */
function useNamedProposal(root: RefObject<HTMLElement | null>) {
  const { hash, key } = useLocation()
  const named = hash.startsWith('#proposal-') ? hash.slice('#proposal-'.length) : null
  useEffect(() => {
    const page = root.current
    if (!named || !page) return
    const scroll = () => {
      const target = [...page.querySelectorAll<HTMLElement>('[id]')].find((element) => element.id === proposalAnchor(named))
      target?.scrollIntoView?.({ block: 'start' })
      return Boolean(target)
    }
    if (scroll()) return
    const observer = new MutationObserver(() => { if (scroll()) observer.disconnect() })
    observer.observe(page, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [root, named, key])
}
